import { z } from 'zod';
import { LineItemChangeDraft } from 'shared/src/domain/line-item/line-item-types';
import { MediaPlan, MediaPlanChanges } from 'shared/src/domain/media-buy/media-buy-types';
import { ISODateString, ISODateStringFromDB } from 'shared/src/date-string-utils/types';

export const Env = z.union([z.literal('dev'), z.literal('staging'), z.literal('prod')]);
export type Env = z.infer<typeof Env>;

export const VerifiedToken = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  exp: z.number(),
  roles: z.array(z.string())
});
export type VerifiedToken = z.infer<typeof VerifiedToken>;

export const DatabaseCreds = z.object({
  username: z.string(),
  password: z.string()
});
export type DatabaseCreds = z.infer<typeof DatabaseCreds>;

export const SnowflakeCreds = z.object({
  username: z.string(),
  role: z.string(),
  private_key: z.string()
});
export type SnowflakeCreds = z.infer<typeof SnowflakeCreds>;

export const PricingMode = z.enum(['price', 'gross_price']);
export type PricingMode = z.infer<typeof PricingMode>;

export const DBStrategy = z.object({
  id: z.string(),
  campaign_id: z.string(),
  start_date: ISODateStringFromDB.nullable(),
  end_date: ISODateStringFromDB.nullable(),
  price: z.coerce.number().nullable(),
  referral_commission_rate: z.coerce.number().default(0),
  effective_price: z.coerce.number().nullable(),
  referral_commission_amount: z.coerce.number().nullable(),
  agency_markup_rate: z.coerce.number().default(0),
  gross_price: z.coerce.number().nullable(),
  pricing_mode: PricingMode.default('price')
});
export type DBStrategy = z.infer<typeof DBStrategy>;

export const StrategyUpdate = z.object({
  start_date: ISODateString,
  end_date: ISODateString,
  price: z.coerce.number(),
  referral_commission_rate: z.number().optional(),
  agency_markup_rate: z.number().optional(),
  pricing_mode: PricingMode.optional()
});
export type StrategyUpdate = z.infer<typeof StrategyUpdate>;

export const DraftStrategyChanges = z.object({
  line_items: z.record(z.string(), LineItemChangeDraft),
  media_plans: z.record(z.string(), MediaPlanChanges),
  strategy_update: StrategyUpdate.optional()
});
export type DraftStrategyChanges = z.infer<typeof DraftStrategyChanges>;

export type CreateMediaPlanOptions = MediaPlan & {
  strategy_id: string;
  line_item_id: string;
};

export const PacingTypes = ['lifetime', 'monthly', 'custom'] as const;
